import type { ReactNode, MouseEvent } from 'react'
import { motion } from 'framer-motion'
import { twMerge } from 'tailwind-merge'
import { useIsMiniContentHovered, useIsMiniDragging } from '../../../atoms'
import { useIsMiniAnchoredLeft } from '../side-context'
import { MINI_REVEAL_TRANSITION, miniRevealVariants } from '../side-motion'
import { useSidepanelState, useSetSidepanelState, useSetSidepanelPeekSource } from '@renderer/overlay/atoms'
import { Button, HighlightLogo, Tooltip } from '@highlight/ui'
import { isDev } from '@shared/utils/platform'

/** The dev build's own mark, which HighlightLogo can't draw. Production uses the brand symbol. */
function MiniHighlightIcon({ size, className }: { size: number; className?: string }) {
  if (isDev) {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 32 32"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={twMerge('text-foreground fill-current opacity-70', className)}
      >
        <path
          d="M0 6.35C0 4.49985 1.49238 3 3.33333 3C5.17428 3 6.66667 4.49985 6.66667 6.35V26.45C6.66667 28.3002 5.17428 29.8 3.33333 29.8C1.49238 29.8 0 28.3002 0 26.45V6.35Z"
          fill="currentColor"
        />
        <path
          d="M25.3333 6.35C25.3333 4.49985 26.8257 3 28.6667 3C30.5076 3 32 4.49985 32 6.35V26.45C32 28.3002 30.5076 29.8 28.6667 29.8C26.8257 29.8 25.3333 28.3002 25.3333 26.45V6.35Z"
          fill="currentColor"
        />
        <path
          d="M18 15.4L11.7513 21.6487C11.62 21.7801 11.4641 21.8843 11.2925 21.9554C11.1209 22.0265 10.937 22.0631 10.7512 22.0631C10.3761 22.0632 10.0163 21.9142 9.751 21.649C9.48569 21.3838 9.33661 21.0241 9.33655 20.6489C9.33649 20.2738 9.48545 19.914 9.75066 19.6487L16 13.4"
          fill="currentColor"
        />
        <path d="M20 17.4L22.6667 14.7334L20 17.4Z" fill="currentColor" />
        <path
          d="M22.3333 15.0667L21.0573 13.7907C20.8073 13.5407 20.6667 13.2016 20.6667 12.848V12.6187C20.6666 12.2651 20.5261 11.926 20.276 11.676L19.1713 10.5714C18.4214 9.82157 17.4045 9.40025 16.344 9.40002H14L14.8287 10.2287C15.5786 10.9788 16 11.996 16 13.0567V14.0667L17.3333 15.4H18.1147C18.4683 15.4001 18.8073 15.5406 19.0573 15.7907L20.3333 17.0667"
          fill="currentColor"
        />
        <path
          d="M18 15.4L11.7513 21.6487C11.62 21.7801 11.4641 21.8843 11.2925 21.9554C11.1209 22.0265 10.937 22.0631 10.7512 22.0631C10.3761 22.0632 10.0163 21.9142 9.751 21.649C9.48569 21.3838 9.33661 21.0241 9.33655 20.6489C9.33649 20.2738 9.48545 19.914 9.75066 19.6487L16 13.4M20 17.4L22.6667 14.7334M22.3333 15.0667L21.0573 13.7907C20.8073 13.5407 20.6667 13.2016 20.6667 12.848V12.6187C20.6666 12.2651 20.5261 11.926 20.276 11.676L19.1713 10.5714C18.4214 9.82157 17.4045 9.40025 16.344 9.40002H14L14.8287 10.2287C15.5786 10.9788 16 11.996 16 13.0567V14.0667L17.3333 15.4H18.1147C18.4683 15.4001 18.8073 15.5406 19.0573 15.7907L20.3333 17.0667"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    )
  }

  return (
    <HighlightLogo size={size} className={twMerge('text-foreground opacity-70', className)}>
      <HighlightLogo.Icon />
    </HighlightLogo>
  )
}

interface MiniLogoProps {
  children?: ReactNode
  className?: string
  onClick?: () => void
  triggerHotkey?: string
  hideLabel?: boolean
  showAudioNoteUI?: boolean
}

export function MiniLogo({
  children,
  className,
  onClick,
  triggerHotkey,
  hideLabel = false,
  showAudioNoteUI = false,
}: MiniLogoProps) {
  const isMiniOnLeftSide = useIsMiniAnchoredLeft()
  const isContentHovered = useIsMiniContentHovered()
  const isDragging = useIsMiniDragging()
  const sidepanelState = useSidepanelState()
  const setSidepanelState = useSetSidepanelState()
  const setPeekSource = useSetSidepanelPeekSource()

  const handleClick = (e: MouseEvent) => {
    e.stopPropagation()
    onClick?.()
  }

  const showLabel = isContentHovered && !hideLabel && !showAudioNoteUI

  return (
    <Tooltip>
      <Tooltip.Trigger
        render={
          <Button
            variant="ghost"
            size="sm"
            className={twMerge(
              // The button's own corners on every side: the drag handle sits beyond it, so no side meets
              // the pill's round end.
              'gap-1',
              !isMiniOnLeftSide && 'flex-row-reverse',
              // Even on both sides, so the mark sits centred in the hover fill.
              'px-1.5',
              onClick &&
                "cursor-pointer after:pointer-events-none after:absolute after:-bottom-3 after:-left-1.5 after:-right-1.5 after:-top-3 after:content-['']",
              className,
            )}
            onClick={onClick ? handleClick : undefined}
            onMouseEnter={() => {
              if (!isDragging && sidepanelState === 'hidden') {
                // Mini hover peeks from far off the right edge, so the chevron must
                // pin in rather than wait on edge proximity (see peek-trigger.tsx).
                setPeekSource('mini')
                setSidepanelState('peeking')
              }
            }}
            onMouseLeave={() => {
              if (!isDragging && sidepanelState === 'peeking') {
                setSidepanelState('hidden')
              }
            }}
          />
        }
      >
        <div className="flex h-5 w-5 shrink-0 items-center justify-center">
          <MiniHighlightIcon size={20} />
        </div>
        {showLabel && (
          <motion.span
            key="ask-label"
            variants={miniRevealVariants(isMiniOnLeftSide)}
            initial="hidden"
            animate="visible"
            transition={MINI_REVEAL_TRANSITION}
            className="text-foreground-placeholder whitespace-nowrap text-sm"
          >
            Ask…
          </motion.span>
        )}
        {children}
      </Tooltip.Trigger>
      <Tooltip.Content sideOffset={14}>
        {`Open Highlight${isDev ? ' Dev' : ''}`}
        {triggerHotkey && <Tooltip.Hotkey hotkey={triggerHotkey} />}
      </Tooltip.Content>
    </Tooltip>
  )
}
